import { defineStore, storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';
import { cloneDeep, isEqual } from 'lodash';

// TODO: Fix old states dependency in enclosed module
import {
  useItineraryStore,
  useRouterStore,
  useStatsigEventsStore,
  useTripStore,
  useActivityDetailsStore,
} from '@apps/main/new-desktop/core/stores';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';

import { SemboDate } from '@libs/sembo-dates';
import { assertValue, isDefined } from '@libs/utils';
import type { TicketRate } from '@libs/trip-planner-interfaces';

import { SELECT_TIME_FOR_ME_ID } from '../models/activityTickets';

import {
  getActivityTotalPrice,
  getSelectedRatesByOffset,
  getIngredientStayDuration,
  getSelectedTicketAvailabilty,
  hasActivityAvailabilityInSuggestedDates,
  getRatesToDisplay,
} from './activityDetailsTicketsStoreLogic';

export const useActivityDetailsTicketsStore = defineStore(
  'ActivityDetailsTicketsStore',
  () => {
    const activityDetailsStore = useActivityDetailsStore();
    const preferredTimeStore = usePreferredTimeStore();
    const { preferredDay } = storeToRefs(preferredTimeStore);
    const tripStore = useTripStore();
    const routerStore = useRouterStore();
    const statsigEventsStore = useStatsigEventsStore();
    const recipeStore = useRecipeStore();
    const itineraryStore = useItineraryStore();

    // State
    const selectedRatesValidityStatus = ref<Record<string, boolean>>({});
    const initialSelectedTickets = cloneDeep(
      recipeStore.selectedActivityTickets
    );
    const selectedDate = ref<SemboDate | undefined>();

    // Getters
    const bookableDays = computed(() => {
      if (!activityDetailsStore.activityDetailsStaticData) {
        return undefined;
      }

      return getIngredientStayDuration(
        activityDetailsStore.activityDetailsStaticData.ingredientDate,
        activityDetailsStore.activityDetailsStaticData.numberOfNights
      );
    });

    const startDate = computed(() => {
      if (!bookableDays.value) {
        return undefined;
      }

      return bookableDays.value[0];
    });

    const selectedDateOffset = computed<number | undefined>(() => {
      if (!selectedDate.value || !startDate.value) {
        return undefined;
      }
      return selectedDate.value.diff(startDate.value, 'day');
    });

    function getSubingredientOffset(): number {
      assertValue(
        activityDetailsStore.activityDetailsStaticData,
        'activityDetailsTicketsStore.ts > getSubingredientOffset > activityDetailsStaticData'
      );
      const subIngredient = recipeStore.getSubIngredientsByCode(
        activityDetailsStore.activityDetailsStaticData.parentIngredientId,
        activityDetailsStore.activityDetailsStaticData.productCode
      )?.[0];

      return subIngredient?.offset ?? 0;
    }

    function isSelectedDate(date: Date): boolean {
      if (!selectedDate.value) {
        return false;
      }
      return selectedDate.value.isSame(date);
    }

    function selectDate(date: Date): void {
      selectedDate.value = new SemboDate(date);
    }

    function thereIsRatesAvailabilityInCurrentMonth(): boolean {
      assertValue(
        activityDetailsStore.activityDetailsDynamicData,
        'activityDetailsTicketsStore.ts > thereIsRatesAvailabilityInCurrentMonth > activityDetailsDynamicData'
      );
      return activityDetailsStore.activityDetailsDynamicData.availability.some(
        (ticket) => !!ticket.rates.length
      );
    }

    function thereIsTicketAvailability(offset: number): boolean {
      assertValue(
        activityDetailsStore.activityDetailsDynamicData,
        'activityDetailsTicketsStore.ts > thereIsTicketAvailability > activityDetailsDynamicData'
      );

      if (!activityDetailsStore.activityDetailsStaticData) {
        return false;
      }

      const activityHasAvailabilityInSuggestedDates =
        hasActivityAvailabilityInSuggestedDates(
          activityDetailsStore.activityDetailsStaticData,
          activityDetailsStore.activityDetailsDynamicData
        );

      return preferredDay.value || activityHasAvailabilityInSuggestedDates
        ? !!getSelectedRatesByOffset(
            offset,
            activityDetailsStore.activityDetailsDynamicData.availability,
            activityDetailsStore.activityDetailsStaticData.ingredientDate
          ).length
        : thereIsRatesAvailabilityInCurrentMonth();
    }

    function updateTicketRateValidityStatus(
      ticketRateValidityStatus: boolean,
      index: number
    ): void {
      const dayIndex = selectedDateOffset.value;
      const rateByDayIndex = `${dayIndex}${index}`;
      selectedRatesValidityStatus.value[rateByDayIndex] =
        ticketRateValidityStatus;
      tripStore.updateAreAllSelectedRatesValid(
        Object.values(selectedRatesValidityStatus.value).every(isDefined)
      );
    }

    async function selectTicket(): Promise<void> {
      assertValue(
        activityDetailsStore.activityDetailsStaticData,
        'activityDetailsTicketsStore.ts > selectTicket > activityDetailsStaticData'
      );
      assertValue(
        recipeStore.selectedActivityTickets,
        'activityDetailsTicketsStore.ts > selectTicket > selectedActivityTickets'
      );

      recipeStore.selectedActivityTickets.forEach((ticket) => {
        if (ticket.timeSlotId === SELECT_TIME_FOR_ME_ID) {
          delete ticket.timeSlotId;
        }
      });

      const availableSelectedActivityTickets =
        recipeStore.selectedActivityTickets.filter((selectedTicket) => {
          assertValue(
            activityDetailsStore.activityDetailsDynamicData,
            'activityDetailsTicketsStore.ts > availableSelectedActivityTickets > activityDetailsDynamicData'
          );
          if (!activityDetailsStore.activityDetailsStaticData) {
            return false;
          }
          return !!getSelectedTicketAvailabilty(
            activityDetailsStore.activityDetailsDynamicData.availability,
            selectedTicket,
            preferredDay.value,
            activityDetailsStore.activityDetailsStaticData.ingredientDate
          );
        });
      statsigEventsStore.logActivityDetailsTicketSelected({
        productCode: activityDetailsStore.activityDetailsStaticData.productCode,
      });
      tripStore.updateActivitySubIngredients(
        activityDetailsStore.activityDetailsStaticData.productCode,
        availableSelectedActivityTickets,
        activityDetailsStore.activityDetailsStaticData.parentIngredientId
      );

      // NOTE: We need to wait for the route to be updated with selected activity before redirecting to overview
      await nextTick();
      await routerStore.navigateToTripOverview();
    }

    function setInitialSelectedDate(): void {
      if (!startDate.value) {
        return;
      }

      selectedDate.value = new SemboDate(startDate.value.toISOString()).add({
        days: getSubingredientOffset(),
      });
    }

    watch(
      () => activityDetailsStore.activityDetailsStaticData,
      (staticData) => {
        if (staticData) {
          setInitialSelectedDate();
        }
      },
      { immediate: true }
    );

    // Getters
    const selectedActivityTotalPrice = computed(() => {
      assertValue(
        activityDetailsStore.activityDetailsStaticData,
        'activityDetailsTicketsStore.ts > selectedActivityTotalPrice > activityDetailsStaticData'
      );
      if (!activityDetailsStore.activityDetailsDynamicData) {
        return undefined;
      }
      return getActivityTotalPrice(
        recipeStore.selectedActivityTickets,
        activityDetailsStore.activityDetailsDynamicData.availability,
        preferredDay.value,
        activityDetailsStore.activityDetailsStaticData.ingredientDate
      );
    });

    const isActivitySelected = computed<boolean>(() => {
      assertValue(
        activityDetailsStore.activityDetailsStaticData,
        'activityDetailsTicketsStore.ts > isActivitySelected > activityDetailsStaticData'
      );

      const subIngredients = recipeStore.getSubIngredientsByCode(
        activityDetailsStore.activityDetailsStaticData.parentIngredientId,
        activityDetailsStore.activityDetailsStaticData.productCode
      );

      return !!subIngredients && subIngredients.length > 0;
    });

    const ticketRatesInSelectedDate = computed<TicketRate[]>(() => {
      assertValue(
        activityDetailsStore.activityDetailsDynamicData,
        'activityDetailsTicketsStore.ts > ticketRatesInSelectedDate > activityDetailsDynamicData'
      );

      if (
        preferredDay.value &&
        activityDetailsStore.activityDetailsStaticData
      ) {
        return getSelectedRatesByOffset(
          selectedDateOffset.value ?? 0,
          activityDetailsStore.activityDetailsDynamicData.availability,
          activityDetailsStore.activityDetailsStaticData.ingredientDate
        );
      } else {
        const ratesForDay = getSelectedRatesByOffset(
          selectedDateOffset.value ?? 0,
          activityDetailsStore.activityDetailsDynamicData.availability,
          itineraryStore.selectedItinerary?.date
        );
        return ratesForDay.length
          ? ratesForDay
          : getRatesToDisplay(
              activityDetailsStore.activityDetailsDynamicData.availability
            );
      }
    });

    const selectedActivityHasChanged = computed(
      () =>
        !isEqual(initialSelectedTickets, recipeStore.selectedActivityTickets)
    );
    const shouldScrollToTicket = computed<boolean>(
      () =>
        !isActivitySelected.value &&
        (!selectedActivityHasChanged.value ||
          !tripStore.areAllSelectedRatesValid)
    );

    return {
      // State
      bookableDays,

      // Actions
      isSelectedDate,
      thereIsTicketAvailability,
      updateTicketRateValidityStatus,
      selectTicket,
      selectDate,

      // Getters
      selectedActivityTotalPrice,
      isActivitySelected,
      ticketRatesInSelectedDate,
      selectedDateOffset,
      shouldScrollToTicket,
    };
  }
);
